/**
 * Protocole d'événements postMessage côté SDK (Phase 4).
 *
 * Ce module duplique volontairement les constantes / types de
 * `src/utils/embedEvents.ts`. Le SDK est compilé en bundle isolé (esbuild IIFE)
 * et ne peut pas importer du code du frontend React sans gonfler le bundle.
 *
 * Un test croisé (`__tests__/events.test.ts`) garantit que les constantes
 * `RTT_WIDGET_EVENT_SOURCE` et `RTT_WIDGET_EVENT_VERSION` restent identiques
 * entre les deux fichiers — toute désynchronisation ferait échouer le test.
 */

/** Identifiant d'origine — DOIT correspondre à `embedEvents.ts`. */
export const RTT_WIDGET_EVENT_SOURCE = 'rintintin-widget' as const;
/** Version du protocole — DOIT correspondre à `embedEvents.ts`. */
export const RTT_WIDGET_EVENT_VERSION = 1 as const;

/** Type union de tous les events possibles. */
export type RintintinWidgetEventType =
  | 'ready'
  | 'resize'
  | 'booking-started'
  | 'cart-confirmed'
  | 'checkout-complete'
  | 'forfait-clicked'
  | 'credits-clicked'
  | 'error';

/** Payload de chaque event, indexé par type (clone de `embedEvents.ts`). */
export interface RintintinWidgetEventPayloads {
  ready: { proSlug: string };
  resize: { height: number };
  'booking-started': {
    serviceId: number | string;
    serviceName: string;
    serviceSlug?: string;
  };
  'cart-confirmed': {
    proSlug: string;
    serviceId: number | string;
    serviceName: string;
    serviceSlug?: string;
    price?: number;
    currency?: string;
    date?: string;
    time?: string;
  };
  'checkout-complete': {
    proSlug?: string;
    bookingIds?: number[];
  };
  'forfait-clicked': {
    forfaitId: number | string;
    forfaitName: string;
    price?: number;
  };
  'credits-clicked': {
    packId: number | string;
    packName: string;
    price?: number;
    creditsAmount?: number;
  };
  error: { message: string; code?: string };
}

/** Forme générique d'un message reçu via postMessage. */
export interface RintintinWidgetEvent<
  T extends RintintinWidgetEventType = RintintinWidgetEventType,
> {
  source: typeof RTT_WIDGET_EVENT_SOURCE;
  type: T;
  version: typeof RTT_WIDGET_EVENT_VERSION;
  payload: RintintinWidgetEventPayloads[T];
}

/**
 * Type guard local SDK : valide la structure du message reçu sans pour autant
 * trust le contenu — c'est au routing de filtrer selon `type`.
 */
export function isWidgetEvent(data: unknown): data is RintintinWidgetEvent {
  if (!data || typeof data !== 'object') return false;
  const candidate = data as Partial<RintintinWidgetEvent>;
  if (candidate.source !== RTT_WIDGET_EVENT_SOURCE) return false;
  if (typeof candidate.type !== 'string') return false;
  if (typeof candidate.version !== 'number') return false;
  if (!candidate.payload || typeof candidate.payload !== 'object') return false;
  return true;
}
